import { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import type { AttendanceMethod, CurrentUser, DashboardPayload, LedgerMemberDayView, LedgerPayload, MonthSessionsPayload, WorkDayView, WorkSessionView } from "../shared/types";
import qrcode from "qrcode-generator";
import { workedMinutesForDay } from "../shared/work-time";
import { api, ClientApiError } from "./api";

type LoadState<T> = { status: "loading" } | { status: "error"; error: string } | { status: "ready"; data: T };

interface AdminOverview {
  organization: {
    id: string;
    name: string;
    slug: string;
    timezone: string;
    ledger_visibility: "self" | "organization";
    work_minutes: number;
    break_minutes: number;
    reminder_lead_minutes: number;
    auto_close_after_minutes: number;
    min_session_minutes: number;
    qr_fallback_enabled: number;
  };
  members: Array<{ id: string; email: string; display_name: string; role: "admin" | "member"; status: "invited" | "active" | "disabled"; slack_user_id: string | null }>;
  tags: Array<{ id: string; public_id: string; name: string; uid_hex: string; active: number; scanBaseUrl: string; qrUrl: string }>;
  integrations: Array<{ id: string; type: string; name: string; enabled: number }>;
  audits: Array<{ id: string; action: string; entity_type: string; created_at: string }>;
}

interface PlatformOverview {
  groups: Array<{
    id: string;
    name: string;
    slug: string;
    timezone: string;
    created_at: string;
    member_count: number;
    admins: Array<{
      id: string;
      email: string;
      display_name: string;
      status: "invited" | "active" | "disabled";
    }>;
    integrations: Array<{ type: string; enabled: number }>;
  }>;
  audits: Array<{ id: string; action: string; entity_type: string; entity_id: string; actor_email: string; created_at: string }>;
}

function formatDate(iso: string, timezone = "Asia/Seoul", dateOnly = false): string {
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: timezone,
    month: "long",
    day: "numeric",
    weekday: dateOnly ? "short" : undefined,
    hour: dateOnly ? undefined : "2-digit",
    minute: dateOnly ? undefined : "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
}

function formatTime(iso: string | null, timezone: string): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
}

function minutesLabel(minutes: number | null): string {
  if (minutes === null) return "진행 중";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours ? `${hours}시간 ${rest}분` : `${rest}분`;
}

function localDateKey(timezone: string, date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function shiftDateKey(dateKey: string, days: number): string {
  const date = new Date(`${dateKey}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function shiftMonthKey(monthKey: string, months: number): string {
  const [year, month] = monthKey.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + months, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** 올해면 "8월", 지난 해면 "2025년 12월"로 줄여 쓴다. */
function monthKeyLabel(monthKey: string, currentMonthKey: string): string {
  const [year, month] = monthKey.split("-");
  return year === currentMonthKey.slice(0, 4) ? `${Number(month)}월` : `${year}년 ${Number(month)}월`;
}

/** 날짜 키는 조직 시간대 기준이라 UTC 정오로 파싱해야 달력 날짜가 유지된다. */
function dayKeyParts(dateKey: string): { title: string; weekday: string } {
  const at = new Date(`${dateKey}T12:00:00Z`);
  return {
    title: new Intl.DateTimeFormat("ko-KR", { timeZone: "UTC", month: "long", day: "numeric" }).format(at),
    weekday: new Intl.DateTimeFormat("ko-KR", { timeZone: "UTC", weekday: "narrow" }).format(at),
  };
}

/** minutesLabel과 달리 정시일 때 "1시간 0분" 대신 "1시간"으로 쓴다. */
function durationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours && rest) return `${hours}시간 ${rest}분`;
  return hours ? `${hours}시간` : `${rest}분`;
}

function dateKeyLabel(dateKey: string): string {
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "UTC",
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(new Date(`${dateKey}T12:00:00Z`));
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') { field += '"'; index += 1; }
      else if (character === '"') quoted = false;
      else field += character;
      continue;
    }
    if (character === '"') quoted = true;
    else if (character === ",") { row.push(field); field = ""; }
    else if (character === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else if (character !== "\r") field += character;
  }
  if (field || row.length) { row.push(field); rows.push(row); }
  return rows;
}

function normalizedHeader(value: string): string {
  return value.replace(/^\uFEFF/, "").trim().toLowerCase().replace(/[^a-z0-9가-힣]/g, "");
}

export function membersFromCsv(text: string): Array<{
  line: number;
  displayName: string;
  email: string;
  slackUserId: string;
  status: string;
  isBot: boolean;
}> {
  const rows = parseCsv(text).filter((row) => row.some((field) => field.trim()));
  if (rows.length < 2) throw new Error("CSV에 헤더와 구성원 행이 필요합니다.");
  const headers = rows[0].map(normalizedHeader);
  const column = (...aliases: string[]) => headers.findIndex((header) => aliases.map(normalizedHeader).includes(header));
  const emailColumn = column("email", "email address", "이메일", "이메일 주소", "메일");
  if (emailColumn < 0) throw new Error("CSV에서 이메일 열을 찾지 못했습니다.");
  const displayNameColumn = column("display_name", "display name", "표시 이름", "표시명");
  const fullNameColumn = column("name", "full name", "real name", "성명", "이름");
  const slackIdColumn = column("slack_user_id", "slack user id", "member id", "user id", "멤버 id", "회원 id");
  const statusColumn = column("status", "account status", "상태", "계정 상태");
  const botColumn = column("is_bot", "is bot", "bot", "봇");
  const typeColumn = column("user type", "member type", "사용자 유형", "멤버 유형");
  const value = (row: string[], index: number) => index >= 0 ? (row[index] ?? "").trim() : "";
  return rows.slice(1).map((row, index) => {
    const botValue = `${value(row, botColumn)} ${value(row, typeColumn)}`.toLowerCase();
    return {
      line: index + 2,
      displayName: value(row, displayNameColumn) || value(row, fullNameColumn),
      email: value(row, emailColumn),
      slackUserId: value(row, slackIdColumn),
      status: value(row, statusColumn),
      isBot: /^(true|1|yes)$/.test(value(row, botColumn).toLowerCase()) || /bot|봇/.test(botValue),
    };
  });
}

function Shell({ user, children }: { user: CurrentUser; children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [organizations, setOrganizations] = useState<Array<{ id: string; name: string; role: "admin" | "member"; active: number }>>([]);
  useEffect(() => {
    api<{ organizations: Array<{ id: string; name: string; role: "admin" | "member"; active: number }> }>("/api/auth/organizations")
      .then((result) => setOrganizations(result.organizations))
      .catch(() => setOrganizations([]));
  }, [user.organizationId]);
  async function logout() {
    await api("/api/auth/logout", { method: "POST", body: "{}" });
    location.assign("/login");
  }
  async function switchOrganization(organizationId: string) {
    if (organizationId === user.organizationId) return;
    await api("/api/auth/switch-organization", {
      method: "POST",
      body: JSON.stringify({ organizationId }),
    });
    location.assign("/");
  }
  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="온타임 홈">
          <span className="brand-mark">온</span>
          <span>온타임</span>
        </a>
        <nav className="desktop-nav" aria-label="주 메뉴">
          <a className={location.pathname === "/" ? "active" : ""} href="/">내 기록</a>
          {user.role === "admin" && <a className={location.pathname === "/ledger" ? "active" : ""} href="/ledger">전체 장부</a>}
          {user.role === "admin" && <a className={location.pathname === "/admin" ? "active" : ""} href="/admin">관리</a>}
          {user.isPlatformAdmin && <a className={location.pathname === "/platform" ? "active" : ""} href="/platform">서비스 관리</a>}
        </nav>
        <button className="profile-button" onClick={() => setMenuOpen((value) => !value)} aria-expanded={menuOpen}>
          <span className="avatar">{user.displayName.slice(0, 1)}</span>
          <span className="profile-copy"><b>{user.displayName}</b><small>{user.organizationName}</small></span>
        </button>
        {menuOpen && <div className="profile-menu">
          <span>{user.email}</span>
          {organizations.length > 1 && <label>현재 그룹<select value={user.organizationId} onChange={(event) => void switchOrganization(event.target.value)}>{organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name} · {organization.role === "admin" ? "관리자" : "구성원"}</option>)}</select></label>}
          <button onClick={logout}>로그아웃</button>
        </div>}
      </header>
      <main>{children}</main>
      <nav className="mobile-nav" aria-label="모바일 메뉴">
        <a href="/" className={location.pathname === "/" ? "active" : ""}>내 기록</a>
        {user.role === "admin" && <a href="/ledger" className={location.pathname === "/ledger" ? "active" : ""}>전체 장부</a>}
        {user.role === "admin" && <a href="/admin" className={location.pathname === "/admin" ? "active" : ""}>관리</a>}
        {user.isPlatformAdmin && <a href="/platform" className={location.pathname === "/platform" ? "active" : ""}>서비스 관리</a>}
      </nav>
    </div>
  );
}

function LoginPage() {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const googleError = new URLSearchParams(location.search).get("googleError");
  const googleErrorMessage = googleError ? ({
    google_not_configured: "Google 로그인이 아직 설정되지 않았습니다.",
    google_access_denied: "Google 로그인이 취소되었습니다.",
    google_account_not_invited: "이 Google 계정은 아직 조직에 초대되지 않았습니다.",
    google_account_conflict: "이 Google 계정은 이미 다른 사용자와 연결되어 있습니다.",
    google_state_invalid: "로그인 요청이 만료되었습니다. 다시 시도해 주세요.",
  }[googleError] ?? "Google 로그인에 실패했습니다. 다시 시도해 주세요.") : "";
  const returnTo = sessionStorage.getItem("attendance:returnTo") || "/";
  const googleLoginUrl = `/api/auth/google/start?${new URLSearchParams({ returnTo })}`;

  async function requestCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api<{ ok: boolean; devCode?: string }>("/api/auth/request-code", {
        method: "POST", body: JSON.stringify({ email }),
      });
      setStep("code");
      setMessage(result.devCode ? `개발용 인증번호: ${result.devCode}` : "메일로 보낸 인증번호를 입력해 주세요.");
      if (result.devCode) setCode(result.devCode);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "인증번호 요청에 실패했습니다.");
    } finally { setBusy(false); }
  }

  async function verifyCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      await api("/api/auth/verify-code", { method: "POST", body: JSON.stringify({ email, code }) });
      const returnTo = sessionStorage.getItem("attendance:returnTo") || "/";
      sessionStorage.removeItem("attendance:returnTo");
      location.assign(returnTo);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "로그인에 실패했습니다.");
    } finally { setBusy(false); }
  }

  return (
    <div className="login-layout">
      <section className="login-story">
        <a className="brand brand-light" href="/"><span className="brand-mark">온</span><span>온타임</span></a>
        <div>
          <p className="eyebrow">내 시간을 정확하게</p>
          <h1>내 근무시간을<br />내가 먼저 압니다.</h1>
          <p>출퇴근 순간을 기록하고, 내 근무시간을 언제든 직접 확인합니다.</p>
        </div>
        <small>위치 정보 수집 없이 · NFC 보안 검증 · 변경 이력 보존</small>
      </section>
      <section className="login-panel">
        <div className="login-card">
          <p className="eyebrow">{step === "email" ? "시작하기" : "이메일 확인"}</p>
          <h2>{step === "email" ? "회사 계정으로 로그인" : "인증번호 6자리"}</h2>
          <p className="muted">{step === "email" ? "초대받은 Google 계정을 사용하세요. 로그인은 30일간 유지됩니다." : `${email}로 인증번호를 보냈습니다.`}</p>
          {step === "email" ? (
            <>
              <a className="google-button" href={googleLoginUrl}><span aria-hidden="true">G</span>Google 계정으로 계속</a>
              <div className="login-divider"><span>또는</span></div>
              <details className="email-login">
                <summary>이메일 인증번호로 로그인</summary>
                <form onSubmit={requestCode}>
                  <label>이메일<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@company.com" required /></label>
                  <button className="secondary wide" disabled={busy}>{busy ? "보내는 중…" : "인증번호 받기"}</button>
                </form>
              </details>
            </>
          ) : (
            <form onSubmit={verifyCode}>
              <label>인증번호<input className="otp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} placeholder="000000" required /></label>
              <button className="primary wide" disabled={busy || code.length !== 6}>{busy ? "확인 중…" : "로그인"}</button>
              <button className="text-button" type="button" onClick={() => setStep("email")}>이메일 다시 입력</button>
            </form>
          )}
          {message && <div className="notice success">{message}</div>}
          {(error || googleErrorMessage) && <div className="notice error">{error || googleErrorMessage}</div>}
        </div>
      </section>
    </div>
  );
}

function TodayHero({ dashboard }: { dashboard: DashboardPayload }) {
  const { openSession, sessions, user } = dashboard;
  // 점심에 찍고 나갔다 온 날은 세션이 여러 개다. 가장 최근 한 건만 보면
  // 출근 시각도 근무시간도 하루치가 되지 않는다.
  const todayKey = localDateKey(user.timezone);
  const todaySessions = sessions.filter((session) => localDateKey(user.timezone, new Date(session.startedAt)) === todayKey);
  const firstIn = todaySessions[todaySessions.length - 1] ?? null;
  const lastOut = todaySessions[0] ?? null;
  const todayWorked = todaySessions.length > 0
    ? workedMinutesForDay(todaySessions, dashboard.breakMinutes, new Date().toISOString())
    : null;
  return (
    <section className="today-hero">
      <div>
        <p className="eyebrow">{formatDate(new Date().toISOString(), user.timezone, true)}</p>
        <h1>{openSession ? "오늘도 잘 해내고 있어요." : "좋은 하루예요."}</h1>
        <p>{openSession ? `예상 퇴근은 ${formatTime(openSession.expectedEndAt, user.timezone)}입니다.` : "NFC 태그를 찍으면 출근 시간이 바로 기록됩니다."}</p>
      </div>
      <div className={`status-pill ${openSession ? "working" : "resting"}`}><span />{openSession ? "근무 중" : "퇴근"}</div>
      <div className="time-grid">
        <div><span>출근</span><strong>{formatTime(firstIn?.startedAt ?? null, user.timezone)}</strong></div>
        <div className="divider" />
        <div><span>퇴근</span><strong>{formatTime(lastOut?.endedAt ?? null, user.timezone)}</strong></div>
        <div className="divider" />
        <div><span>근무</span><strong className="small-strong">{openSession ? "진행 중" : minutesLabel(todayWorked)}</strong></div>
      </div>
    </section>
  );
}

/**
 * 퇴근 태깅 없이 정책이 추정한 마감은 급여 근거로 그대로 쓸 수 없으므로
 * 장부와 개인 기록 양쪽에서 실제 태깅과 구분해 보여준다.
 */
function isEstimated(session: WorkSessionView): boolean {
  return session.closeReason === "auto" || session.closeReason === "backfill";
}

/**
 * 장부와 개인 월간 기록이 공유하는 하루치 근무 행. 왼쪽 식별자(구성원 이름
 * 또는 날짜)만 다르고 출퇴근 시각·목표 대비 진행·타임라인은 동일하다.
 */
function WorkDayRow({ avatar, title, subtitle, sessions, targetMinutes, breakMinutes, timezone, now, ariaLabel }: {
  avatar: string;
  title: string;
  subtitle: string;
  sessions: WorkSessionView[];
  targetMinutes: number;
  breakMinutes: number;
  timezone: string;
  now: number;
  ariaLabel: string;
}) {
  const hasOpenSession = sessions.some((session) => !session.endedAt);
  const sessionRanges = sessions.map((session) => {
    const startedAt = new Date(session.startedAt).getTime();
    const endedAt = session.endedAt ? new Date(session.endedAt).getTime() : now;
    return { session, startedAt, endedAt: Math.max(startedAt, endedAt) };
  });
  // 재실 시간에는 휴게가 들어 있다. 목표와 견줄 값은 휴게를 뺀 근무시간이다.
  const workedMinutes = workedMinutesForDay(sessions, breakMinutes, new Date(now).toISOString());
  const goalMinutes = Math.max(1, targetMinutes);
  const remainingMinutes = Math.max(0, goalMinutes - workedMinutes);
  const overtimeMinutes = Math.max(0, workedMinutes - goalMinutes);
  const status = remainingMinutes > 0
    ? `${durationLabel(remainingMinutes)} ${hasOpenSession ? "남음" : "부족"}`
    : overtimeMinutes > 0
      ? `목표 달성 · ${durationLabel(overtimeMinutes)} 초과`
      : "목표 달성";
  const timelineStart = Math.min(...sessionRanges.map((range) => range.startedAt));
  const plannedEnd = new Date(sessions[0].expectedEndAt).getTime();
  const activityEnd = Math.max(...sessionRanges.map((range) => range.endedAt));
  const timelineEnd = Math.max(timelineStart + 60 * 60_000, plannedEnd, activityEnd);
  const timelineDuration = timelineEnd - timelineStart;
  return (
    <article className={`ledger-session ${hasOpenSession ? "working" : "complete"}`}>
      <div className="ledger-person">
        <span className="ledger-avatar">{avatar}</span>
        <div><b>{title}</b><small>{subtitle}</small></div>
      </div>
      <div className="ledger-clock-list">
        {sessions.map((session, index) => <div className="ledger-clock" key={session.id}>
          <span><small>{index + 1}차 출근</small><b>{formatTime(session.startedAt, timezone)}</b></span>
          <i>→</i>
          <span><small>{index + 1}차 퇴근{isEstimated(session) ? " (추정)" : ""}</small><b>{formatTime(session.endedAt, timezone)}</b></span>
        </div>)}
      </div>
      <div className="ledger-progress">
        <div className="ledger-progress-copy">
          <span><b>{durationLabel(workedMinutes)}</b> / 목표 {durationLabel(goalMinutes)}</span>
          <strong className={remainingMinutes > 0 && !hasOpenSession ? "short" : ""}>{status}</strong>
        </div>
        <div className="work-timeline" role="img" aria-label={ariaLabel}>
          {sessionRanges.map((range, index) => <span
            className={`${range.session.endedAt ? "closed" : "open"} segment-${index % 2}`}
            key={range.session.id}
            style={{
              left: `${((range.startedAt - timelineStart) / timelineDuration) * 100}%`,
              width: `${Math.max(0.5, ((range.endedAt - range.startedAt) / timelineDuration) * 100)}%`,
            }}
            title={`${formatTime(range.session.startedAt, timezone)}–${formatTime(range.session.endedAt, timezone)}`}
          />)}
        </div>
        <div className="timeline-axis">
          <span>{formatTime(new Date(timelineStart).toISOString(), timezone)}</span>
          <small>{breakMinutes > 0 ? `휴게 ${durationLabel(breakMinutes)}` : ""}</small>
          <span>{formatTime(new Date(timelineEnd).toISOString(), timezone)}</span>
        </div>
      </div>
    </article>
  );
}

function daySubtitle(sessions: WorkSessionView[], openLabel: string, closedLabel: string): string {
  const estimatedCount = sessions.filter(isEstimated).length;
  const state = sessions.some((session) => !session.endedAt) ? openLabel : closedLabel;
  const notes = [
    estimatedCount > 0 ? `자동 마감 ${estimatedCount}건` : "",
    sessions.some((session) => session.usedQr) ? "QR 기록" : "",
  ].filter(Boolean);
  return [`${sessions.length}회`, state, ...notes].join(" · ");
}

function LedgerMemberRow({ member, breakMinutes, timezone, now }: {
  member: LedgerMemberDayView;
  breakMinutes: number;
  timezone: string;
  now: number;
}) {
  return <WorkDayRow
    avatar={member.memberName.slice(0, 1)}
    title={member.memberName}
    subtitle={daySubtitle(member.sessions, "현재 근무 중", "근무 완료")}
    sessions={member.sessions}
    targetMinutes={member.targetMinutes}
    breakMinutes={breakMinutes}
    timezone={timezone}
    now={now}
    ariaLabel={`${member.memberName}님의 ${member.sessions.length}개 근무 구간`}
  />;
}

function MonthDayRow({ day, targetMinutes, breakMinutes, timezone, now }: {
  day: WorkDayView;
  targetMinutes: number;
  breakMinutes: number;
  timezone: string;
  now: number;
}) {
  const { title, weekday } = dayKeyParts(day.date);
  return <WorkDayRow
    avatar={weekday}
    title={title}
    subtitle={daySubtitle(day.sessions, "근무 중", "근무 완료")}
    sessions={day.sessions}
    targetMinutes={targetMinutes}
    breakMinutes={breakMinutes}
    timezone={timezone}
    now={now}
    ariaLabel={`${title}의 ${day.sessions.length}개 근무 구간`}
  />;
}

function MonthSessions({ user }: { user: CurrentUser }) {
  const currentMonth = useMemo(() => localDateKey(user.timezone).slice(0, 7), [user.timezone]);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [state, setState] = useState<LoadState<MonthSessionsPayload>>({ status: "loading" });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    setState({ status: "loading" });
    api<MonthSessionsPayload>(`/api/my-sessions?${new URLSearchParams({ month: selectedMonth })}`)
      .then((data) => setState({ status: "ready", data }))
      .catch((caught) => setState({ status: "error", error: caught instanceof Error ? caught.message : "근무 기록을 불러오지 못했습니다." }));
  }, [selectedMonth]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const days = state.status === "ready" ? state.data.days : [];
  return (
    <section className="panel">
      <div className="section-heading">
        <div><p className="eyebrow">내 근무 기록</p><h2>{monthKeyLabel(selectedMonth, currentMonth)}</h2></div>
        <div className="month-nav" aria-label="월 선택">
          <button type="button" aria-label="이전 달" onClick={() => setSelectedMonth((month) => shiftMonthKey(month, -1))}>‹</button>
          <span>{days.length}일</span>
          <button type="button" aria-label="다음 달" disabled={selectedMonth >= currentMonth} onClick={() => setSelectedMonth((month) => shiftMonthKey(month, 1))}>›</button>
        </div>
      </div>
      {state.status === "loading" && <div className="ledger-loading"><div className="spinner" /><p>근무 기록을 불러오는 중…</p></div>}
      {state.status === "error" && <div className="empty">{state.error}</div>}
      {state.status === "ready" && days.length === 0 && <div className="empty">이 달에는 근무 기록이 없습니다.</div>}
      {state.status === "ready" && <div className="ledger-session-list">
        {days.map((day) => <MonthDayRow
          key={day.date}
          day={day}
          targetMinutes={state.data.targetMinutes}
          breakMinutes={state.data.breakMinutes}
          timezone={user.timezone}
          now={now}
        />)}
      </div>}
    </section>
  );
}

function Ledger({ user }: { user: CurrentUser }) {
  const today = useMemo(() => localDateKey(user.timezone), [user.timezone]);
  const [selectedDate, setSelectedDate] = useState(today);
  const [state, setState] = useState<LoadState<LedgerPayload>>({ status: "loading" });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    setState({ status: "loading" });
    api<LedgerPayload>(`/api/ledger?${new URLSearchParams({ date: selectedDate })}`)
      .then((data) => setState({ status: "ready", data }))
      .catch((caught) => setState({ status: "error", error: caught instanceof Error ? caught.message : "장부를 불러오지 못했습니다." }));
  }, [selectedDate]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const members = state.status === "ready" ? state.data.members : [];
  return (
    <section className="page-content">
      <div className="page-title ledger-title"><div><p className="eyebrow">관리자 전용</p><h1>전체 근무 장부</h1><p>운영 확인이 필요한 경우에만 구성원의 날짜별 근무 기록을 확인합니다.</p></div>
        <button className="today-button" type="button" onClick={() => setSelectedDate(today)} disabled={selectedDate === today}>오늘</button>
      </div>
      <div className="ledger-date-nav" aria-label="장부 날짜 선택">
        <button type="button" aria-label="이전 날짜" onClick={() => setSelectedDate((date) => shiftDateKey(date, -1))}>‹</button>
        <label><span>조회 날짜</span><input type="date" value={selectedDate} max={today} onChange={(event) => event.target.value && setSelectedDate(event.target.value)} /></label>
        <button type="button" aria-label="다음 날짜" disabled={selectedDate >= today} onClick={() => setSelectedDate((date) => shiftDateKey(date, 1))}>›</button>
      </div>
      <div className="panel ledger-panel">
        <div className="ledger-day-heading"><div><p className="eyebrow">일일 근무 현황</p><h2>{dateKeyLabel(selectedDate)}</h2></div><span>{members.length}명</span></div>
        {state.status === "loading" && <div className="ledger-loading"><div className="spinner" /><p>장부를 불러오는 중…</p></div>}
        {state.status === "error" && <div className="empty">{state.error}</div>}
        {state.status === "ready" && members.length === 0 && <div className="empty">이 날짜에는 근무 기록이 없습니다.</div>}
        {state.status === "ready" && <div className="ledger-session-list">{members.map((member) => <LedgerMemberRow key={member.memberId} member={member} breakMinutes={state.data.breakMinutes} timezone={user.timezone} now={now} />)}</div>}
      </div>
    </section>
  );
}

function DashboardPage({ data }: { data: DashboardPayload }) {
  return <Shell user={data.user}><div className="page-content"><TodayHero dashboard={data} /><MonthSessions user={data.user} /></div></Shell>;
}

interface ScanResult {
  action: "check_in" | "check_out";
  occurredAt: string;
  expectedEndAt?: string;
  startedAt?: string;
  tagName: string;
  todayWorkedMinutes?: number;
  autoClosedPrevious?: { endedAt: string };
  weekly?: { workedMinutes: number; targetMinutes: number };
}

/**
 * 라이브러리 없이 CSS만으로 그려지는 체크. 로티나 GIF와 달리 번들이 늘지
 * 않고, 자체 SVG라 스캔 화면의 엄격한 CSP를 그대로 둘 수 있다.
 */
function SuccessMark() {
  return (
    <div className="success-mark" aria-hidden="true">
      <svg viewBox="0 0 84 84" role="presentation">
        <circle className="success-mark-ring" cx="42" cy="42" r="38" />
        <path className="success-mark-check" d="M25 43.5 L37 55 L59 31" />
      </svg>
    </div>
  );
}

function WeeklyProgress({ weekly }: { weekly: { workedMinutes: number; targetMinutes: number } }) {
  const target = Math.max(1, weekly.targetMinutes);
  const ratio = Math.min(1, weekly.workedMinutes / target);
  const remaining = Math.max(0, target - weekly.workedMinutes);
  return (
    <div className="weekly-progress">
      <div className="weekly-progress-copy">
        <span>이번 주 <b>{durationLabel(weekly.workedMinutes)}</b></span>
        <small>{remaining > 0 ? `${durationLabel(remaining)} 남음` : "주 목표 달성"}</small>
      </div>
      <div className="work-bar"><span style={{ width: `${Math.max(2, ratio * 100)}%` }} /></div>
      <div className="weekly-progress-scale"><small>주 목표 {durationLabel(target)}</small></div>
    </div>
  );
}

function ScanPage({ user, method }: { user: CurrentUser; method: AttendanceMethod }) {
  const [state, setState] = useState<"ready" | "saving" | "done" | "error">("ready");
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<ScanResult | null>(null);
  const params = useMemo(() => new URLSearchParams(location.search), []);
  const tagPublicId = location.pathname.split("/").filter(Boolean)[1];
  const piccData = params.get("picc_data") ?? params.get("e");
  const cmac = params.get("cmac") ?? params.get("c");
  const encryptedFileData = params.get("enc") ?? undefined;
  // QR에는 SUN 값이 없다. 태그 식별자만 있으면 유효한 링크다.
  const valid = method === "qr" ? Boolean(tagPublicId) : Boolean(tagPublicId && piccData && cmac);
  const channelLabel = method === "qr" ? "QR" : "NFC";

  async function submit() {
    if (!valid) return;
    if (method === "nfc" && (!piccData || !cmac)) return;
    setState("saving"); setMessage("");
    try {
      const response = await api<ScanResult>("/api/attendance/toggle", {
        method: "POST",
        body: JSON.stringify({ tagPublicId, piccData, cmac, encryptedFileData, method }),
      });
      setResult(response); setState("done");
      history.replaceState({}, "", "/");
    } catch (caught) {
      const text = caught instanceof Error ? caught.message : "출퇴근 처리에 실패했습니다.";
      setMessage(text); setState("error");
    }
  }

  return (
    <div className="scan-layout">
      <a className="brand" href="/"><span className="brand-mark">온</span><span>온타임</span></a>
      <div className="scan-card">
        {state === "done" && result ? <>
          <SuccessMark />
          <p className="eyebrow">기록 완료</p>
          <h1>{result.action === "check_in" ? "출근했습니다." : "오늘도 수고했어요."}</h1>
          <div className="receipt">
            <span>{result.action === "check_in" ? "예상 퇴근" : "오늘 근무"}</span>
            <strong>{result.action === "check_in"
              ? formatTime(result.expectedEndAt ?? null, user.timezone)
              : durationLabel(result.todayWorkedMinutes ?? 0)}</strong>
            <small>{result.action === "check_in"
              ? `출근 ${formatTime(result.occurredAt, user.timezone)} · ${channelLabel} ${result.tagName}`
              : `${formatTime(result.startedAt ?? null, user.timezone)} → ${formatTime(result.occurredAt, user.timezone)} · ${channelLabel} ${result.tagName}`}</small>
          </div>
          {result.weekly && <WeeklyProgress weekly={result.weekly} />}
          {result.autoClosedPrevious && <div className="notice error">이전 근무에 퇴근 기록이 없어 예상 퇴근 시각({formatTime(result.autoClosedPrevious.endedAt, user.timezone)})으로 자동 마감했습니다. 실제와 다르면 관리자에게 정정을 요청해 주세요.</div>}
          <a className="primary button-link" href="/">내 기록 보기</a>
        </> : <>
          <div className="nfc-glyph"><span>)))</span></div>
          <p className="eyebrow">{channelLabel} 출퇴근</p>
          <h1>{valid ? `${user.displayName}님, 기록할까요?` : "태그 링크를 확인해 주세요."}</h1>
          <p className="muted">{method === "qr"
            ? "NFC를 쓰기 어려울 때를 위한 대체 경로입니다. 기록에 QR로 표시됩니다."
            : "등록된 보안 태그의 SUN 값을 검증해 출퇴근을 기록합니다."}</p>
          <button className="primary wide large" onClick={submit} disabled={!valid || state === "saving"}>
            {state === "saving" ? (method === "qr" ? "기록하는 중…" : "SUN 보안 검증 중…") : "출퇴근 기록하기"}
          </button>
          {state === "error" && <div className="notice error">{message}</div>}
          {!valid && <div className="notice error">{method === "qr" ? "태그 식별자가 없는 링크입니다." : "PICCData 또는 CMAC이 없는 링크입니다."}</div>}
        </>}
      </div>
      <small className="privacy-note">위치 정보는 요청하거나 저장하지 않습니다.</small>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label>{label}{children}</label>;
}

function AdminPage({ user }: { user: CurrentUser }) {
  const [state, setState] = useState<LoadState<AdminOverview>>({ status: "loading" });
  const [notice, setNotice] = useState("");
  const [noticeError, setNoticeError] = useState(false);
  const load = useCallback(async () => {
    try { setState({ status: "ready", data: await api<AdminOverview>("/api/admin/overview") }); }
    catch (caught) { setState({ status: "error", error: caught instanceof Error ? caught.message : "관리자 정보를 불러오지 못했습니다." }); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function submit(path: string, method: string, form: HTMLFormElement, transform: (data: FormData) => object) {
    const data = new FormData(form); setNotice(""); setNoticeError(false);
    try {
      await api(path, { method, body: JSON.stringify(transform(data)) });
      form.reset(); setNotice("저장했습니다."); await load();
    } catch (caught) { setNoticeError(true); setNotice(caught instanceof Error ? caught.message : "저장하지 못했습니다."); }
  }

  async function importCsv(form: HTMLFormElement) {
    const input = form.elements.namedItem("csv") as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) { setNoticeError(true); setNotice("CSV 파일을 선택해 주세요."); return; }
    setNotice(""); setNoticeError(false);
    try {
      const members = membersFromCsv(await file.text());
      const result = await api<{ imported: number; skipped: number; issues: Array<{ line: number; reason: string }> }>("/api/admin/members/import", {
        method: "POST",
        body: JSON.stringify({ members }),
      });
      form.reset();
      const issuePreview = result.issues.slice(0, 3).map((issue) => `${issue.line}행 ${issue.reason}`).join(", ");
      setNotice(`${result.imported}명 초대 · ${result.skipped}명 건너뜀${issuePreview ? ` (${issuePreview}${result.issues.length > 3 ? " 외" : ""})` : ""}`);
      await load();
    } catch (caught) {
      setNoticeError(true);
      setNotice(caught instanceof Error ? caught.message : "CSV를 가져오지 못했습니다.");
    }
  }

  function downloadCsvTemplate() {
    const blob = new Blob(["\uFEFFname,email,slack_user_id\n홍길동,hong@example.com,U012ABCDEF\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url; anchor.download = "ontime-members-template.csv"; anchor.click();
    URL.revokeObjectURL(url);
  }

  if (state.status === "loading") return <Shell user={user}><Loading /></Shell>;
  if (state.status === "error") return <Shell user={user}><ErrorState message={state.error} /></Shell>;
  const data = state.data;
  return (
    <Shell user={user}><div className="page-content admin-page">
      <div className="page-title"><p className="eyebrow">현재 그룹 설정</p><h1>{user.organizationName}</h1><p>이 그룹의 근무 정책, 구성원, 보안 태그와 알림만 관리합니다.</p></div>
      {notice && <div className={`notice ${noticeError ? "error" : "success"}`}>{notice}</div>}
      <div className="admin-grid">
        <section className="panel admin-card span-2"><div className="section-heading"><div><p className="eyebrow">그룹</p><h2>근무 정책</h2></div></div>
          <form className="form-grid" onSubmit={(event) => { event.preventDefault(); void submit("/api/admin/organization", "PATCH", event.currentTarget, (form) => ({ name: form.get("name"), timezone: form.get("timezone"), ledgerVisibility: form.get("visibility"), workMinutes: Number(form.get("workMinutes")), breakMinutes: Number(form.get("breakMinutes")), reminderLeadMinutes: Number(form.get("reminderLeadMinutes")), autoCloseAfterMinutes: Number(form.get("autoCloseAfterMinutes")), minSessionMinutes: Number(form.get("minSessionMinutes")), qrFallbackEnabled: form.get("qrFallbackEnabled") === "on" })); }}>
            <Field label="그룹명"><input name="name" defaultValue={data.organization.name} required /></Field>
            <Field label="시간대"><input name="timezone" defaultValue={data.organization.timezone} required /></Field>
            <Field label="근무시간(분)"><input name="workMinutes" type="number" defaultValue={data.organization.work_minutes} min="1" required /></Field>
            <Field label="휴게시간(분)"><input name="breakMinutes" type="number" defaultValue={data.organization.break_minutes} min="0" required /></Field>
            <Field label="퇴근 알림(분 전)"><input name="reminderLeadMinutes" type="number" defaultValue={data.organization.reminder_lead_minutes} min="0" required /></Field>
            <Field label="미퇴근 자동 마감(분)"><input name="autoCloseAfterMinutes" type="number" defaultValue={data.organization.auto_close_after_minutes} min="0" required /></Field>
            <Field label="최소 근무 인정(분)"><input name="minSessionMinutes" type="number" defaultValue={data.organization.min_session_minutes} min="0" required /></Field>
            <Field label="QR 출퇴근 허용"><label className="toggle-field"><input name="qrFallbackEnabled" type="checkbox" defaultChecked={Boolean(data.organization.qr_fallback_enabled)} /><span>NFC를 쓰기 어려운 구성원을 위한 대체 경로. SUN 검증이 없어 기록에 QR로 표시됩니다.</span></label></Field>
            <Field label="장부 열람"><input type="hidden" name="visibility" value="self" /><input value="관리자 전용" readOnly /></Field>
            <button className="primary">정책 저장</button>
          </form>
        </section>

        <section className="panel admin-card"><div className="section-heading"><div><p className="eyebrow">NTAG 424 DNA</p><h2>보안 태그 등록</h2></div><a className="secondary qr-print-link" href="/admin/qr">QR 인쇄</a><span className="count">{data.tags.length}</span></div>
          <form onSubmit={(event) => { event.preventDefault(); void submit("/api/admin/tags", "POST", event.currentTarget, (form) => ({ name: form.get("name"), uidHex: form.get("uidHex"), metaReadKey: form.get("metaReadKey"), fileReadKey: form.get("fileReadKey") })); }}>
            <Field label="태그명"><input name="name" placeholder="출퇴근 태그" required /></Field><Field label="UID (7바이트 hex)"><input name="uidHex" placeholder="04DE5F1EACC040" required /></Field><Field label="SDM Meta Read Key"><input name="metaReadKey" type="password" autoComplete="off" placeholder="32자리 hex" required /></Field><Field label="SDM File Read Key"><input name="fileReadKey" type="password" autoComplete="off" placeholder="32자리 hex" required /></Field><button className="secondary wide">태그 등록</button>
          </form>
          <div className="compact-list">{data.tags.map((item) => <div key={item.id}><b>{item.name}</b><small>UID {item.uid_hex}</small><code>{item.scanBaseUrl}</code><code>{item.qrUrl}</code></div>)}</div>
        </section>

        <section className="panel admin-card"><div className="section-heading"><div><p className="eyebrow">구성원</p><h2>초대</h2></div><span className="count">{data.members.length}</span></div>
          <form onSubmit={(event) => { event.preventDefault(); void submit("/api/admin/members", "POST", event.currentTarget, (form) => ({ displayName: form.get("displayName"), email: form.get("email"), role: form.get("role"), slackUserId: form.get("slackUserId") })); }}>
            <Field label="이름"><input name="displayName" required /></Field><Field label="이메일"><input name="email" type="email" required /></Field><Field label="Slack User ID"><input name="slackUserId" placeholder="U012ABC" /></Field><input type="hidden" name="role" value="member" /><button className="secondary wide">구성원 초대</button>
          </form>
          <details className="bulk-import">
            <summary>CSV로 한꺼번에 초대</summary>
            <p className="form-help">온타임 양식 또는 Slack의 전체 멤버 CSV를 그대로 올릴 수 있습니다. 비활성 계정·봇·중복 이메일은 건너뜁니다.</p>
            <form onSubmit={(event) => { event.preventDefault(); void importCsv(event.currentTarget); }}>
              <Field label="CSV 파일"><input name="csv" type="file" accept=".csv,text/csv" required /></Field>
              <div className="two-fields"><button className="secondary" type="button" onClick={downloadCsvTemplate}>양식 다운로드</button><button className="primary">CSV 가져오기</button></div>
            </form>
          </details>
          <div className="member-editor-list">{data.members.map((item) => {
            const isSelf = item.id === user.membershipId;
            return <details className="member-editor" key={item.id}>
              <summary><span><b>{item.display_name}</b><small>{item.email} · {item.role === "admin" ? "관리자" : "구성원"} · {item.status === "active" ? "활성" : item.status === "invited" ? "초대됨" : "비활성"}</small></span><em>{item.slack_user_id ? "Slack 연결됨" : "편집"}</em></summary>
              <form onSubmit={(event) => { event.preventDefault(); void submit(`/api/admin/members/${item.id}`, "PATCH", event.currentTarget, (form) => ({ displayName: form.get("displayName"), role: form.get("role") ?? item.role, status: form.get("status") ?? item.status, slackUserId: form.get("slackUserId") })); }}>
                <Field label="이름"><input name="displayName" defaultValue={item.display_name} required /></Field>
                <Field label="이메일"><input value={item.email} readOnly /></Field>
                <div className="two-fields"><Field label="권한"><input value={item.role === "admin" ? "관리자" : "구성원"} readOnly /><input type="hidden" name="role" value={item.role} /></Field><Field label="상태"><select name="status" defaultValue={item.status} disabled={isSelf}><option value="invited">초대됨</option><option value="active">활성</option><option value="disabled">비활성</option></select></Field></div>
                <Field label="Slack User ID"><input name="slackUserId" defaultValue={item.slack_user_id ?? ""} placeholder="U012ABCDEF" autoCapitalize="characters" /></Field>
                {isSelf && <small className="form-help">현재 로그인한 계정의 관리자 권한과 활성 상태는 변경할 수 없습니다.</small>}
                <button className="secondary wide">구성원 정보 저장</button>
              </form>
            </details>;
          })}</div>
        </section>

        <section className="panel admin-card"><div className="section-heading"><div><p className="eyebrow">선택 연동</p><h2>Slack</h2></div>{data.integrations.some((item) => item.type === "slack") && <span className="connected">연결됨</span>}</div>
          <p className="muted compact">출퇴근 채널에는 Incoming Webhook, 개인 DM에는 Bot Token을 사용합니다. 둘 중 하나만 설정해도 됩니다.</p>
          <form onSubmit={(event) => { event.preventDefault(); void submit("/api/admin/integrations/slack", "PUT", event.currentTarget, (form) => ({ webhookUrl: form.get("webhookUrl"), botToken: form.get("botToken") })); }}>
            <Field label="Incoming Webhook URL"><input name="webhookUrl" type="password" autoComplete="off" placeholder="https://hooks.slack.com/services/…" /></Field><Field label="Bot Token"><input name="botToken" type="password" autoComplete="off" placeholder="xoxb-…" /></Field><button className="secondary wide">Slack 연결 저장</button>
          </form>
        </section>

        <section className="panel admin-card"><div className="section-heading"><div><p className="eyebrow">선택 연동</p><h2>Discord</h2></div>{data.integrations.some((item) => item.type === "discord") && <span className="connected">연결됨</span>}</div>
          <p className="muted compact">Discord 채널의 Incoming Webhook URL을 등록하면 출퇴근 기록을 해당 채널로 전송합니다.</p>
          <form onSubmit={(event) => { event.preventDefault(); void submit("/api/admin/integrations/discord", "PUT", event.currentTarget, (form) => ({ webhookUrl: form.get("webhookUrl") })); }}>
            <Field label="Incoming Webhook URL"><input name="webhookUrl" type="password" autoComplete="off" placeholder="https://discord.com/api/webhooks/…" required /></Field><button className="secondary wide">Discord 연결 저장</button>
          </form>
        </section>
      </div>
    </div></Shell>
  );
}

function PlatformPage({ user }: { user: CurrentUser }) {
  const [state, setState] = useState<LoadState<PlatformOverview>>({ status: "loading" });
  const [notice, setNotice] = useState("");
  const [noticeError, setNoticeError] = useState(false);
  const load = useCallback(async () => {
    try { setState({ status: "ready", data: await api<PlatformOverview>("/api/platform/overview") }); }
    catch (caught) { setState({ status: "error", error: caught instanceof Error ? caught.message : "서비스 정보를 불러오지 못했습니다." }); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function submit(path: string, form: HTMLFormElement, payload: object) {
    setNotice(""); setNoticeError(false);
    try {
      await api(path, { method: "POST", body: JSON.stringify(payload) });
      form.reset(); setNotice("초대했습니다."); await load();
    } catch (caught) {
      setNoticeError(true);
      setNotice(caught instanceof Error ? caught.message : "처리하지 못했습니다.");
    }
  }

  if (state.status === "loading") return <Shell user={user}><Loading /></Shell>;
  if (state.status === "error") return <Shell user={user}><ErrorState message={state.error} /></Shell>;
  return (
    <Shell user={user}><div className="page-content admin-page platform-page">
      <div className="page-title"><p className="eyebrow">서비스 관리자</p><h1>그룹 관리</h1><p>회사를 그룹 단위로 분리하고, 각 그룹을 맡을 관리자를 초대합니다.</p></div>
      {notice && <div className={`notice ${noticeError ? "error" : "success"}`}>{notice}</div>}
      <section className="panel platform-create">
        <div className="section-heading"><div><p className="eyebrow">새 테넌트</p><h2>그룹과 첫 관리자 만들기</h2></div></div>
        <form className="form-grid" onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          void submit("/api/platform/groups", event.currentTarget, {
            name: form.get("name"),
            timezone: form.get("timezone"),
            adminName: form.get("adminName"),
            adminEmail: form.get("adminEmail"),
          });
        }}>
          <Field label="그룹명"><input name="name" placeholder="회사 또는 팀 이름" required /></Field>
          <Field label="시간대"><input name="timezone" defaultValue="Asia/Seoul" required /></Field>
          <Field label="그룹 관리자 이름"><input name="adminName" required /></Field>
          <Field label="그룹 관리자 이메일"><input name="adminEmail" type="email" required /></Field>
          <button className="primary">그룹 생성 및 초대</button>
        </form>
      </section>

      <div className="platform-group-list">
        {state.data.groups.map((group) => (
          <section className="panel platform-group" key={group.id}>
            <div className="section-heading"><div><p className="eyebrow">{group.slug}</p><h2>{group.name}</h2></div><span className="count">{group.member_count}</span></div>
            <div className="platform-meta"><span>{group.timezone}</span><span>구성원 {group.member_count}명</span><span>{group.integrations.length ? group.integrations.map((item) => item.type).join(" · ") : "연동 없음"}</span></div>
            <div className="platform-admins">
              {group.admins.map((admin) => <div key={admin.id}><b>{admin.display_name}</b><small>{admin.email} · {admin.status === "active" ? "활성" : admin.status === "invited" ? "초대됨" : "비활성"}</small></div>)}
            </div>
            <details className="platform-invite">
              <summary>그룹 관리자 추가 초대</summary>
              <form onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                void submit(`/api/platform/groups/${group.id}/admins`, event.currentTarget, {
                  displayName: form.get("displayName"),
                  email: form.get("email"),
                });
              }}>
                <Field label="이름"><input name="displayName" required /></Field>
                <Field label="이메일"><input name="email" type="email" required /></Field>
                <button className="secondary">관리자 초대</button>
              </form>
            </details>
          </section>
        ))}
        {state.data.groups.length === 0 && <div className="panel empty">아직 생성된 그룹이 없습니다.</div>}
      </div>
    </div></Shell>
  );
}

// 인쇄용 정적 QR. 태그 옆에 붙여 NFC가 인식되지 않을 때의 대체 경로로 쓴다.
// 화면 캡처 대신 SVG로 그려야 인쇄 배율과 관계없이 모듈 경계가 선명하다.
const QR_QUIET_ZONE = 4;
// 로고가 가리는 한 변의 비율. H 등급은 코드워드의 약 30%까지 복원하므로
// 여백 포함 면적 6% 남짓이면 폰 카메라가 안정적으로 읽는다.
const QR_LOGO_RATIO = 0.22;

function QrCode({ value, logo }: { value: string; logo?: string }) {
  const { size, count, path } = useMemo(() => {
    const code = qrcode(0, logo ? "H" : "M");
    code.addData(value);
    code.make();
    const moduleCount = code.getModuleCount();
    let d = "";
    for (let row = 0; row < moduleCount; row += 1) {
      for (let col = 0; col < moduleCount; col += 1) {
        if (code.isDark(row, col)) d += `M${col + QR_QUIET_ZONE} ${row + QR_QUIET_ZONE}h1v1h-1z`;
      }
    }
    return { size: moduleCount + QR_QUIET_ZONE * 2, count: moduleCount, path: d };
  }, [value, logo]);
  const logoSize = count * QR_LOGO_RATIO;
  const pad = 1;
  const logoOrigin = (size - logoSize) / 2;
  return (
    <svg className="qr-code" viewBox={`0 0 ${size} ${size}`} shapeRendering="crispEdges" role="img" aria-label={value}>
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#000" />
      {logo && <>
        <rect x={logoOrigin - pad} y={logoOrigin - pad} width={logoSize + pad * 2} height={logoSize + pad * 2} rx={1.2} fill="#fff" shapeRendering="geometricPrecision" />
        <image href={logo} x={logoOrigin} y={logoOrigin} width={logoSize} height={logoSize} preserveAspectRatio="xMidYMid meet" />
      </>}
    </svg>
  );
}

interface QrPrintSettings {
  headerTop: string;
  headerTitle: string;
  footer: string;
  showUrl: boolean;
  showTagName: boolean;
  logo: string;
}

const QR_PRINT_STORAGE_KEY = "attendance:qrPrint";

function loadQrPrintSettings(): Partial<QrPrintSettings> {
  try { return JSON.parse(localStorage.getItem(QR_PRINT_STORAGE_KEY) ?? "{}") as Partial<QrPrintSettings>; }
  catch { return {}; }
}

// 로고는 브라우저에 저장하므로 긴 변 512px PNG로 줄여 저장 공간을 아낀다.
function readLogo(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("로고 파일을 읽지 못했습니다."));
    reader.onload = () => {
      const source = String(reader.result);
      if (file.type === "image/svg+xml") { resolve(source); return; }
      const image = new Image();
      image.onerror = () => reject(new Error("이미지 형식을 읽지 못했습니다."));
      image.onload = () => {
        const scale = Math.min(1, 512 / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        canvas.getContext("2d")?.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      image.src = source;
    };
    reader.readAsDataURL(file);
  });
}

function QrPrintPage() {
  const [state, setState] = useState<LoadState<AdminOverview>>({ status: "loading" });
  const [layout, setLayout] = useState<"large" | "small">("large");
  const [settings, setSettings] = useState<Partial<QrPrintSettings>>(loadQrPrintSettings);
  const [logoError, setLogoError] = useState("");
  useEffect(() => {
    api<AdminOverview>("/api/admin/overview")
      .then((data) => setState({ status: "ready", data }))
      .catch((caught) => setState({ status: "error", error: caught instanceof Error ? caught.message : "태그 정보를 불러오지 못했습니다." }));
  }, []);
  useEffect(() => {
    try { localStorage.setItem(QR_PRINT_STORAGE_KEY, JSON.stringify(settings)); }
    catch { /* 저장하지 못해도 이번 인쇄에는 지장이 없다. */ }
  }, [settings]);

  if (state.status === "loading") return <Loading />;
  if (state.status === "error") return <ErrorState message={state.error} />;
  const { organization, tags } = state.data;
  const activeTags = tags.filter((tag) => tag.active);
  const view: QrPrintSettings = {
    headerTop: settings.headerTop ?? "",
    headerTitle: settings.headerTitle ?? "출퇴근 QR",
    footer: settings.footer ?? "NFC 태그가 인식되지 않을 때\n휴대폰 카메라로 찍어 주세요.",
    showUrl: settings.showUrl ?? false,
    showTagName: settings.showTagName ?? false,
    logo: settings.logo ?? "",
  };
  const update = (patch: Partial<QrPrintSettings>) => setSettings((current) => ({ ...current, ...patch }));

  async function chooseLogo(file: File | undefined) {
    if (!file) return;
    setLogoError("");
    try { update({ logo: await readLogo(file) }); }
    catch (caught) { setLogoError(caught instanceof Error ? caught.message : "로고를 넣지 못했습니다."); }
  }

  return (
    <div className="qr-print">
      <div className="qr-print-toolbar">
        <a className="secondary" href="/admin">← 관리자</a>
        <div className="qr-print-layout">
          <button className={layout === "large" ? "primary" : "secondary"} onClick={() => setLayout("large")}>한 장에 1개</button>
          <button className={layout === "small" ? "primary" : "secondary"} onClick={() => setLayout("small")}>한 장에 4개</button>
        </div>
        <button className="primary" onClick={() => window.print()} disabled={activeTags.length === 0}>인쇄</button>
      </div>
      <section className="panel qr-print-editor">
        <Field label="머리글 윗줄"><input value={view.headerTop} onChange={(event) => update({ headerTop: event.target.value })} placeholder="비워 두면 표시하지 않음" /></Field>
        <Field label="머리글 제목"><input value={view.headerTitle} onChange={(event) => update({ headerTitle: event.target.value })} placeholder="비워 두면 표시하지 않음" /></Field>
        <Field label="바닥글"><textarea rows={2} value={view.footer} onChange={(event) => update({ footer: event.target.value })} placeholder="비워 두면 표시하지 않음" /></Field>
        <Field label="가운데 로고">
          <div className="qr-logo-picker">
            {view.logo && <img src={view.logo} alt="" />}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(event) => { void chooseLogo(event.target.files?.[0]); event.target.value = ""; }} />
            {view.logo && <button type="button" className="secondary" onClick={() => update({ logo: "" })}>로고 빼기</button>}
          </div>
        </Field>
        <label className="toggle-field"><input type="checkbox" checked={view.showUrl} onChange={(event) => update({ showUrl: event.target.checked })} /><span>QR 아래에 주소 표시</span></label>
        <label className="toggle-field"><input type="checkbox" checked={view.showTagName} onChange={(event) => update({ showTagName: event.target.checked })} /><span>태그명 표시 (태그가 여러 개일 때 구분용)</span></label>
        <button type="button" className="secondary" onClick={() => setSettings({})}>기본값으로</button>
        {logoError && <div className="notice error">{logoError}</div>}
        <small className="muted">입력한 내용은 이 브라우저에만 저장됩니다. 로고는 정사각형에 가까운 이미지가 가장 잘 보이며, 인쇄 후 한 번 찍어서 인식되는지 확인해 주세요.</small>
      </section>
      {!organization.qr_fallback_enabled && <div className="notice error qr-print-warning">이 그룹은 QR 출퇴근이 꺼져 있습니다. 인쇄해 붙여도 기록되지 않으니 관리자 화면 근무 정책에서 먼저 켜 주세요.</div>}
      {activeTags.length === 0 && <div className="notice error qr-print-warning">인쇄할 활성 태그가 없습니다.</div>}
      <div className={`qr-sheet ${layout}`}>
        {activeTags.map((tag) => (
          <article key={tag.id} className="qr-card">
            {view.headerTop && <p className="qr-card-org">{view.headerTop}</p>}
            {view.headerTitle && <h1>{view.headerTitle}</h1>}
            <QrCode value={tag.qrUrl} logo={view.logo || undefined} />
            {view.showTagName && <p className="qr-card-tag">{tag.name}</p>}
            {view.footer && <p className="qr-card-help">{view.footer}</p>}
            {view.showUrl && <code>{tag.qrUrl}</code>}
          </article>
        ))}
      </div>
    </div>
  );
}

function Loading() { return <div className="state-page"><div className="spinner" /><p>기록을 불러오는 중…</p></div>; }
function ErrorState({ message }: { message: string }) { return <div className="state-page"><h2>불러오지 못했습니다.</h2><p>{message}</p><button className="primary" onClick={() => location.reload()}>다시 시도</button></div>; }

export function App() {
  const [state, setState] = useState<LoadState<DashboardPayload>>({ status: "loading" });
  const path = location.pathname;
  useEffect(() => {
    api<DashboardPayload>("/api/dashboard")
      .then((data) => setState({ status: "ready", data }))
      .catch((caught) => {
        if (caught instanceof ClientApiError && caught.status === 401) {
          if (path.startsWith("/s/") || path.startsWith("/q/")) sessionStorage.setItem("attendance:returnTo", `${path}${location.search}`);
          if (path !== "/login") location.replace("/login");
          else setState({ status: "error", error: "AUTH_REQUIRED" });
          return;
        }
        setState({ status: "error", error: caught instanceof Error ? caught.message : "서비스를 불러오지 못했습니다." });
      });
  }, [path]);

  if (path === "/login" && (state.status === "error" && state.error === "AUTH_REQUIRED")) return <LoginPage />;
  if (state.status === "loading") return <Loading />;
  if (state.status === "error") return <ErrorState message={state.error} />;
  if (path === "/login") { location.replace("/"); return <Loading />; }
  if (path.startsWith("/s/")) return <ScanPage user={state.data.user} method="nfc" />;
  if (path.startsWith("/q/")) return <ScanPage user={state.data.user} method="qr" />;
  if (path === "/platform" && state.data.user.isPlatformAdmin) return <PlatformPage user={state.data.user} />;
  if (path === "/platform") { location.replace("/"); return <Loading />; }
  if (path === "/admin" && state.data.user.role === "admin") return <AdminPage user={state.data.user} />;
  if (path === "/admin/qr" && state.data.user.role === "admin") return <QrPrintPage />;
  if (path === "/ledger" && state.data.user.role === "admin") return <Shell user={state.data.user}><Ledger user={state.data.user} /></Shell>;
  if (path === "/ledger") { location.replace("/"); return <Loading />; }
  return <DashboardPage data={state.data} />;
}
